import { useState } from 'react';

import type { TicketRow, ContactRow, ChannelRow } from '../api/queries.js';
import {
  ChannelIcon,
  IconButton,
  PriorityBar,
  SLATimer,
  Skeleton,
  Tag,
  UnreadDot,
  timeAgo,
  type ChannelType,
  type TicketPriority,
} from '../components/ui/index.js';

interface Props {
  tickets: TicketRow[] | undefined;
  loading: boolean;
  activeId: string | null;
  onSelect: (id: string) => void;
  contactsById: Map<string, ContactRow>;
  channelsById: Map<string, ChannelRow>;
  /** Названия магазинов: в общем списке у строки подписан её магазин */
  storeNames?: Map<string, string>;
  onAssignSelf?: (ticketId: string) => void;
  onClose?: (ticketId: string) => void;
  currentUserId?: string;
}

export function TicketList(props: Props): JSX.Element {
  if (props.loading) {
    return (
      <div className="inbx-tlist">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="inbx-tlist__row" style={{ cursor: 'default' }}>
            <Skeleton width={3} height={32} radius={2} />
            <div style={{ flex: 1 }}>
              <Skeleton height={14} width="70%" />
              <div style={{ height: 6 }} />
              <Skeleton height={11} width="90%" />
              <div style={{ height: 6 }} />
              <Skeleton height={11} width="50%" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!props.tickets || props.tickets.length === 0) {
    return (
      <div className="inbx-tlist">
        <div className="inbx-empty" style={{ padding: '40px 20px' }}>
          <i className="ti-inbox inbx-empty__icon" />
          <div className="inbx-empty__title">Тикетов нет</div>
          <div className="inbx-empty__desc">Все обращения обработаны</div>
        </div>
      </div>
    );
  }

  return (
    <div className="inbx-tlist">
      {props.tickets.map((t) => (
        <TicketRowItem
          key={t.id}
          ticket={t}
          isActive={props.activeId === t.id}
          contact={props.contactsById.get(t.contactId)}
          channel={props.channelsById.get(t.channelId)}
          storeName={t.storeId ? props.storeNames?.get(t.storeId) : undefined}
          onSelect={() => props.onSelect(t.id)}
          onAssignSelf={
            !t.assignedTo && props.onAssignSelf ? () => props.onAssignSelf!(t.id) : undefined
          }
          onClose={props.onClose ? () => props.onClose!(t.id) : undefined}
        />
      ))}
    </div>
  );
}

function TicketRowItem({
  ticket,
  isActive,
  contact,
  channel,
  storeName,
  onSelect,
  onAssignSelf,
  onClose,
}: {
  ticket: TicketRow;
  isActive: boolean;
  contact: ContactRow | undefined;
  channel: ChannelRow | undefined;
  storeName?: string;
  onSelect: () => void;
  onAssignSelf?: () => void;
  onClose?: () => void;
}): JSX.Element {
  const [hovered, setHovered] = useState(false);
  const channelType = (channel?.type ?? 'custom') as ChannelType;
  const name = contact?.name ?? 'Анонимный клиент';
  const preview = ticket.subject ?? 'без темы';
  // «Ждёт ответа» = последнее публичное сообщение от клиента (API считает в list).
  // Это сильнее, чем «нет первого ответа» — подсвечиваем КАЖДЫЙ раз, когда
  // клиент написал, а оператор ещё не ответил, не только при создании тикета.
  const awaitingReply = Boolean(ticket.awaiting_reply);
  return (
    <div
      className={`inbx-tlist__row ${isActive ? 'is-active' : ''} ${awaitingReply ? 'is-awaiting' : ''}`}
      onClick={onSelect}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <PriorityBar priority={(ticket.priority as TicketPriority) ?? 'low'} />
      <div className="inbx-tlist__main">
        <div className="inbx-tlist__top">
          {awaitingReply && <UnreadDot />}
          <span
            className="inbx-tlist__name"
            style={awaitingReply ? { fontWeight: 600, color: 'var(--inbx-text)' } : undefined}
            title={awaitingReply ? 'Ждёт ответа оператора' : undefined}
          >
            {name}
          </span>
          <span className="inbx-tlist__time">
            {timeAgo(ticket.lastMessageAt ?? ticket.createdAt)}
          </span>
        </div>
        <div className="inbx-tlist__channel">
          <ChannelIcon channel={channelType} size={14} />
          <span className="inbx-tlist__channel-label">
            {storeName ? `${storeName} · ` : ''}
            {channel?.name ?? channelType} · #{ticket.number}
          </span>
        </div>
        <div className="inbx-tlist__preview">{preview}</div>
        <div className="inbx-tlist__bottom">
          {ticket.tags.slice(0, 2).map((tag) => (
            <Tag key={tag}>{tag}</Tag>
          ))}
          <span className="inbx-tlist__sla">
            <SLATimer deadline={ticket.slaDeadline} breached={ticket.slaBreached} />
          </span>
        </div>
      </div>
      {hovered && (
        <div className="inbx-tlist__actions">
          {onAssignSelf && (
            <IconButton
              icon="ti-user-plus"
              tooltip="Назначить себе"
              onClick={(e) => {
                e.stopPropagation();
                onAssignSelf();
              }}
            />
          )}
          {onClose && (
            <IconButton
              icon="ti-archive"
              tooltip="Закрыть"
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}
